import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useNavigationType } from 'react-router';
import { useAppSessionState, type AppSessionState } from '../../api/connections';
import { useStore } from '../../lib/store';
import { player } from '../../player/engine';
import { useDesktopLayout } from '../theme/layout';
import { DesktopSidebar } from './DesktopSidebar';
import { LayerErrorBoundary } from './LayerErrorBoundary';
import { bindNavigation, unbindNavigation, useTabSwitchPending, type LayerInfo } from './nav';
import { needsReduce, reduceNavigator, type NavigatorState, type NavLocation } from './navigator';
import { NavigatorView, type NavigatorChrome } from './NavigatorView';
import { classifyLocation, decideGate, gatePath, tabOfBase, type GateId, type RouteClass } from './routes';
import { DesktopPlayerBar, DevGallery, NoConnectionsGate, OnboardingScreen, PaneRoutes, PopOutHost, preloadGates, preloadSignInLanding, whenIdle } from './screens';
import { ShellLayer } from './ShellLayer';
import { SignedInEffects } from './SignedInEffects';
import { useShellKey } from './useShellKey';
import styles from './AppNavigator.module.css';

const DEV = import.meta.env.DEV;

function readHistoryIdx(): number | null {
  const state: unknown = window.history.state;
  const idx = state && typeof state === 'object' ? (state as Record<string, unknown>).idx : undefined;
  return typeof idx === 'number' ? idx : null;
}

/** The gate the session asks for; kept while a switch started from a gate is verifying. */
function useGate(sessionState: AppSessionState): GateId | null {
  const last = useRef<GateId>('welcome');
  let gate: GateId | null = null;
  if (sessionState === 'noActiveConnectionConfigured') gate = 'no-connections';
  else if (sessionState === 'firstRunNoServerConfigured' || sessionState === 'unauthenticatedOnActiveServer') gate = 'welcome';
  else if (sessionState === 'switchingServer') gate = last.current;
  if (gate) last.current = gate;
  return gate;
}

function layerInfo(state: NavigatorState, gate: GateId | null): LayerInfo {
  const top = state.entries[state.entries.length - 1];
  const base = state.baseRoute;
  const info: LayerInfo = {
    kind: base.kind,
    tab: tabOfBase(base),
    overlay: base.kind === 'overlay' ? base.overlay : null,
    page: null,
    modal: null,
    gate,
    depth: state.entries.length,
    pathname: top?.location.pathname ?? state.base.pathname,
  };
  if (top?.route.kind === 'pushed') return { ...info, kind: 'pushed', page: top.route.page };
  if (top?.route.kind === 'modal') return { ...info, kind: 'modal', modal: top.route.modal };
  if (gate) return { ...info, kind: 'gate', pathname: gatePath(gate) };
  return info;
}

function GateLayer({ gate, location, obscured }: { gate: GateId; location: NavLocation; obscured: boolean }) {
  const Screen = gate === 'no-connections' ? NoConnectionsGate : OnboardingScreen;
  return (
    <div className={[styles.gate, obscured ? styles.obscured : ''].filter(Boolean).join(' ')} role="main">
      <LayerErrorBoundary>
        <Suspense fallback={<div className={styles.fallback} aria-busy="true" />}>
          <PaneRoutes location={location} Screen={Screen} />
        </Suspense>
      </LayerErrorBoundary>
    </div>
  );
}

/** Desktop layout: the now-playing bar along the bottom of the window while a book is loaded. */
function DesktopPlayerStrip() {
  return (
    // Its own box, so an error view stays inside the bar's strip.
    <div className={styles.playerBar}>
      <LayerErrorBoundary>
        <Suspense fallback={null}>
          <DesktopPlayerBar />
        </Suspense>
      </LayerErrorBoundary>
    </div>
  );
}

function DevGalleryPage() {
  if (!DevGallery) return null;
  return (
    <Suspense fallback={null}>
      <DevGallery />
    </Suspense>
  );
}

/**
 * AuthWrapper + MainScaffold routing (spec 01 §5.4/§7.2, contract §4): turns
 * the URL into a gate or the signed-in shell plus the navigator stack.
 */
export function AppNavigator() {
  const location = useLocation() as NavLocation;
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const sessionState = useAppSessionState();
  const signedIn = sessionState === 'authenticated';
  const gate = useGate(sessionState);
  const shellKey = useShellKey();
  const desktop = useDesktopLayout();
  const hasBook = useStore(player.store, (s) => s.itemId !== null);

  const route: RouteClass = classifyLocation(location, { signedIn, dev: DEV });
  const decision = decideGate({ route, location, gate: signedIn ? null : gate });
  const renders = decision.kind === 'render' && route.kind !== 'dev';

  // `state` is the navigator state for the committed URL; `shown` is what is drawn.
  // They differ only while an overlay → tab switch is between its two history
  // steps: the overlay stays on screen until the tab replaces the entry the
  // Back landed on, as Flutter swaps them in a single frame.
  const tabSwitchPending = useTabSwitchPending();
  const [view, setView] = useState<{ nav: NavigatorState; shown: NavigatorState } | null>(null);
  let state = view?.nav ?? null;
  if (renders && (!state || needsReduce(state, location, signedIn))) {
    state = reduceNavigator(state, { location, navigationType, historyIdx: readHistoryIdx(), signedIn, dev: DEV });
  }
  const shown = view && signedIn && tabSwitchPending ? view.shown : state;
  if (state && shown && (state !== view?.nav || shown !== view.shown)) setView({ nav: state, shown });

  useLayoutEffect(() => {
    if (!renders || !state || !shown) return;
    bindNavigation({
      navigate,
      location,
      route,
      base: state.base,
      activeTab: tabOfBase(shown.baseRoute),
      layer: layerInfo(shown, signedIn ? null : gate),
    });
  });

  useEffect(() => () => unbindNavigation(navigate), [navigate]);

  useEffect(() => {
    if (signedIn) return;
    preloadGates();
    return whenIdle(preloadSignInLanding);
  }, [signedIn]);

  if (decision.kind === 'redirect') return <Navigate to={decision.to} replace state={decision.state} />;
  if (route.kind === 'dev') return <DevGalleryPage />;
  if (!shown) return null;

  const current = shown;
  const chrome: NavigatorChrome | undefined =
    signedIn && desktop ? { sidebar: <DesktopSidebar />, playerBar: hasBook ? <DesktopPlayerStrip /> : null } : undefined;
  const gateLocation: NavLocation =
    route.kind === 'gate' ? location : { pathname: gatePath(gate ?? 'welcome'), search: '', hash: '', state: null, key: `gate:${gate}` };

  return (
    <>
      {signedIn && <SignedInEffects scope={shellKey} />}
      {/* The pop-out player's window lives as long as the book, in either layout (it renders no DOM here). */}
      {signedIn && hasBook && PopOutHost && (
        <Suspense fallback={null}>
          <PopOutHost />
        </Suspense>
      )}
      <NavigatorView
        state={current}
        chrome={chrome}
        renderBase={({ interactive, obscured }) =>
          signedIn ? (
            <ShellLayer key={shellKey} base={current.base} baseRoute={current.baseRoute} interactive={interactive} obscured={obscured} />
          ) : (
            <GateLayer gate={gate ?? 'welcome'} location={gateLocation} obscured={obscured} />
          )
        }
      />
    </>
  );
}
